// /api/native/v1/interview calls (面试模拟).
// Contract: platform/docs/api-inventory/content-ops.md section 2. Errors are {error:"CODE", message} as ApiError.
import type { Call } from './call'

export type InterviewMode = 'specialty' | 'mock'
export type InterviewStatus = 'in_progress' | 'interrupted' | 'completed'
export type RecordingStatus = 'recorded' | 'none' | 'interrupted'
export type InterviewRating = 'unrated' | 'skilled' | 'improve' | 'unable'

export interface InterviewCategory { id: string; title: string }

export interface InterviewRegion {
  id: string
  name: string
  year: number | null
  language: string
  ready: boolean
  contentVersion: string | null
  categories: InterviewCategory[]
  sourceUrl: string | null
  legacyUrl: string | null
  mockAvailable: boolean
  practiceNotice: string
}

export interface InterviewCatalog {
  version: string
  enabled: boolean
  regions: InterviewRegion[]
}

export interface InterviewQuestion {
  id: string
  categoryId?: string
  section?: string
  title?: string
  prompt?: string
  preparationSeconds: number
  answerSeconds: number
  timingSource?: 'official' | string
}

export interface InterviewSelectionGroup {
  id: string
  title?: string
  chooseCount: number
  candidates: InterviewQuestion[]
}

export interface InterviewAnswer {
  questionId: string
  elapsedMs: number
  completed: boolean
  recordingStatus: RecordingStatus
  rating: InterviewRating
}

export interface InterviewSession {
  id: string
  clientRequestId: string
  regionId: string
  regionName: string
  year: number | null
  language: string
  mode: InterviewMode
  categoryId: string | null
  contentVersion: string | null
  status: InterviewStatus
  revision: number
  createdAt: string
  updatedAt: string
  questions: InterviewQuestion[]
  selectionGroups: InterviewSelectionGroup[]
  answers: InterviewAnswer[]
}

export interface InterviewReview {
  sessionId: string
  items: { questionId: string; referencePoints: string[] }[]
}

export interface CreateInterviewSession {
  /** Idempotency key, ^[A-Za-z0-9][A-Za-z0-9:_-]{0,127}$. Reuse it only for a retry of the same request. */
  clientRequestId: string
  regionId: string
  mode: InterviewMode
  categoryId?: string
}

const ROOT = '/api/native/v1/interview'

export function createInterviewApi(call: Call) {
  const one = async (request: Parameters<Call>[0]) => {
    const body = await call<{ session: InterviewSession } | InterviewSession>(request)
    return 'session' in body ? body.session : body
  }
  return {
    catalog: () => call<InterviewCatalog>({ method: 'GET', path: `${ROOT}/catalog` }),
    sessions: () => call<{ sessions: InterviewSession[] }>({ method: 'GET', path: `${ROOT}/sessions` }),
    createSession: (input: CreateInterviewSession) => one({ method: 'POST', path: `${ROOT}/sessions`, body: input }),
    session: (id: string) => one({ method: 'GET', path: `${ROOT}/sessions/${encodeURIComponent(id)}` }),
    /** revision must be the current revision + 1. */
    saveSession: (id: string, input: { revision: number; status: InterviewStatus; answers: InterviewAnswer[] }) =>
      one({ method: 'PUT', path: `${ROOT}/sessions/${encodeURIComponent(id)}`, body: input }),
    selectQuestions: (id: string, input: { revision: number; selections: { groupId: string; questionIds: string[] }[] }) =>
      one({ method: 'POST', path: `${ROOT}/sessions/${encodeURIComponent(id)}/select`, body: input }),
    review: (id: string) => call<InterviewReview>({ method: 'GET', path: `${ROOT}/sessions/${encodeURIComponent(id)}/review` }),
    deleteSession: (id: string) => call<{ ok: true }>({ method: 'DELETE', path: `${ROOT}/sessions/${encodeURIComponent(id)}` }),
  }
}
